import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SignatureProvider } from './components/SignatureSystem';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PageTransition from './components/PageTransition';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import BackToTop from './components/BackToTop';
import Breadcrumbs from './components/Breadcrumbs';
import { initBackgroundParticles } from './utils/backgroundParticles';
import ProjectDetails from './components/ProjectDetails';

// Lazy load pages
const Index = React.lazy(() => import('./pages/Index'));
const HxscPortal = React.lazy(() => import('./pages/HxscPortal'));
const SxscPortal = React.lazy(() => import('./pages/SxscPortal'));
const About = React.lazy(() => import('./pages/About'));
const Learn = React.lazy(() => import('./pages/Learn'));
const TrustSafety = React.lazy(() => import('./pages/TrustSafety'));
const AboutOwner = React.lazy(() => import('./pages/AboutOwner'));
const Spark = React.lazy(() => import('./pages/Spark'));
const SparkLite = React.lazy(() => import('./pages/SparkLite'));
const Souls = React.lazy(() => import('./pages/Souls'));

const App: React.FC = () => {
  // Remove SignatureAchievements and SignatureHelper since we're using the portal system now
  useEffect(() => {
    initBackgroundParticles();
  }, []);

  const ProjectDetailsWrapper: React.FC = () => {
    const { name } = useParams<{ name: string }>();

    const projectDetails = {
      spark: {
        name: 'Spark',
        description: 'AI-powered, full-stack web development with multi-LLM support and next-gen extensibility.',
        mainLink: '/spark',
        externalUrl: 'https://soulcloude-spark.pages.dev/',
      },
      'spark-lite': {
        name: 'Spark Lite',
        description: 'A lightweight version of Spark for smaller projects and devices.',
        mainLink: '/spark-lite',
        externalUrl: 'https://spark-lite.vercel.app/',
      },
      upcoming: {
        name: 'Upcoming',
        description: 'Stay tuned for our upcoming projects that will revolutionize the way we interact with AI. More exciting projects are in development!',
        mainLink: '/souls',
        externalUrl: null,
      },
    };

    const project = projectDetails[name?.toLowerCase() as keyof typeof projectDetails];

    if (!project) {
      return <div>Project not found</div>;
    }

    return (
      <ProjectDetails 
        name={project.name} 
        description={project.description} 
        mainLink={project.mainLink}
        externalUrl={project.externalUrl}
      />
    );
  };

  return (
    <SignatureProvider>
      <Router>
        <ErrorBoundary>
          <div className="min-h-screen bg-sc-dark text-white">
          <Navbar />
          <main className="relative z-10">
            <div className="w-full">
              <Breadcrumbs />
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                 <Route
                    path="/"
                    element={
                      <PageTransition>
                        <Index />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/souls"
                    element={
                      <PageTransition>
                        <Souls />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/about"
                    element={
                      <PageTransition>
                        <About />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/learn"
                    element={
                      <PageTransition>
                        <Learn />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/trust"
                    element={
                      <PageTransition>
                        <TrustSafety />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/trust-safety"
                    element={
                      <PageTransition>
                        <TrustSafety />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/owner"
                    element={
                      <PageTransition>
                        <AboutOwner />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/spark"
                    element={
                      <PageTransition>
                        <Spark />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/spark-lite"
                    element={
                      <PageTransition>
                        <SparkLite />
                      </PageTransition>
                    }
                  />
                   <Route
                    path="/project/:name"
                    element={
                      <PageTransition>
                        <ProjectDetailsWrapper />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/hxsc-portal"
                    element={
                      <PageTransition>
                        <HxscPortal />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/sxsc-portal"
                    element={
                      <PageTransition>
                        <SxscPortal />
                      </PageTransition>
                    }
                  />
                </Routes>
              </Suspense>
            </div >
          </main>
          <BackToTop />
          <Footer />
          </div>
        </ErrorBoundary>
      </Router>
    </SignatureProvider>
  );
};

export default App;
